// src/adminpage/AdminManagementPage.js
import { useState, useEffect, useCallback, useRef } from "react";
import {
    Search, Plus, Shield, ShieldCheck, UserCog, User,
    ChevronLeft, ChevronRight, RefreshCw, MoreVertical,
    Edit2, Power, ArrowUpDown, X, Eye, EyeOff, Lock,
    AlertTriangle, CheckCircle,
} from "lucide-react";
import AdminLayout from "../components/admin/AdminLayout";
import AdminManagementService from "../services/admin/AdminManagementService";
import { useNotification } from "../context/NotificationContext";
import { useAdminAuth } from "../services/admin/AdminAuthServices";
import { useAdminPermissions } from "../hooks/useAdminPermissions";
import { useNavigate } from "react-router-dom";

// ── Role config ──────────────────────────────────────────────
const ROLE_CONFIG = {
    super_admin: {
        label: "Super Admin",
        color: "text-purple-700 bg-purple-100 border-purple-200 dark:text-purple-300 dark:bg-purple-900/30 dark:border-purple-800",
        icon: ShieldCheck,
        rank: 3,
    },
    admin: {
        label: "Admin",
        color: "text-blue-700 bg-blue-100 border-blue-200 dark:text-blue-300 dark:bg-blue-900/30 dark:border-blue-800",
        icon: UserCog,
        rank: 2,
    },
    staff: {
        label: "Staff",
        color: "text-green-700 bg-green-100 border-green-200 dark:text-green-300 dark:bg-green-900/30 dark:border-green-800",
        icon: User,
        rank: 1,
    },
};

const SYSTEM_SA_COLOR =
    "text-red-700 bg-red-100 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-700";

const STATUS_CONFIG = {
    active: { label: "Active", color: "text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/20" },
    suspended: { label: "Suspended", color: "text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-900/20" },
};

// ── Helpers ──────────────────────────────────────────────────
function callerRank(admin) {
    if (!admin) return 0;
    if (admin.is_system_default) return 4;
    return { staff: 1, admin: 2, super_admin: 3 }[admin.role] || 0;
}

function targetRank(row) {
    if (row.is_system_default) return 4;
    return { staff: 1, admin: 2, super_admin: 3 }[row.role] || 0;
}

function canActOn(caller, row) {
    if (!caller || !row) return false;
    if (row.is_system_default) return false;
    if (caller.id === row.id) return false;
    return callerRank(caller) > targetRank(row);
}

function allowedRolesForCreate(caller) {
    const rank = callerRank(caller);
    const roles = [];
    if (rank >= 2) roles.push("staff");
    if (rank >= 3) roles.push("admin");
    // rank 4 (system SA) can also promote to super_admin
    if (rank >= 4) roles.push("super_admin");
    return roles;
}

function allowedRolesForChange(caller, target) {
    const cRank = callerRank(caller);
    return Object.entries(ROLE_CONFIG)
        .filter(([, cfg]) => cfg.rank < cRank && cfg.rank !== targetRank(target))
        .map(([key]) => key);
}

// ── Role Badge ───────────────────────────────────────────────
function RoleBadge({ row }) {
    if (row.is_system_default && row.role === "super_admin") {
        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border ${SYSTEM_SA_COLOR}`}>
                <Shield size={11} />
                System Super Admin
            </span>
        );
    }
    const cfg = ROLE_CONFIG[row.role] || ROLE_CONFIG.staff;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border ${cfg.color}`}>
            <Icon size={11} />
            {cfg.label}
        </span>
    );
}

// ── Stat Card ────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#F3F4F6] dark:border-[#222] p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
                <Icon size={22} />
            </div>
            <div>
                <p className="text-2xl font-bold text-[#111] dark:text-white">{value ?? "—"}</p>
                <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">{label}</p>
            </div>
        </div>
    );
}

// ── Create / Edit Modal ──────────────────────────────────────
function AdminModal({ mode, initial, callerAdmin, onClose, onSave }) {
    const [form, setForm] = useState({
        full_name: initial?.full_name || "",
        email: initial?.email || "",
        password: "",
        role: initial?.role || "staff",
    });
    const [showPwd, setShowPwd] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const roles = mode === "create"
        ? allowedRolesForCreate(callerAdmin)
        : [];          // edit doesn't change role through this modal

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSaving(true);
        try {
            const payload = mode === "create"
                ? form
                : { full_name: form.full_name, email: form.email };
            await onSave(payload);
        } catch (err) {
            setError(err?.response?.data?.message || "An error occurred.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-2xl w-full max-w-md border border-[#E5E7EB] dark:border-[#2A2A2A]">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6] dark:border-[#222]">
                    <h2 className="font-bold text-[#111] dark:text-white text-base">
                        {mode === "create" ? "Create Admin Account" : "Edit Admin"}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-[#262626] rounded-xl transition-colors">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl px-4 py-3 border border-red-200 dark:border-red-800">
                            <AlertTriangle size={16} className="flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {/* Full Name */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wide">Full Name</label>
                        <input
                            required
                            value={form.full_name}
                            onChange={e => set("full_name", e.target.value)}
                            placeholder="Jane Doe"
                            className="w-full px-4 py-2.5 rounded-xl text-sm bg-[#F9FAFB] dark:bg-[#111] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f]/30 focus:border-[#62397f]"
                        />
                    </div>

                    {/* Email */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wide">Email</label>
                        <div className="relative flex items-center">
                            <input
                                required
                                type="text"
                                value={form.email.split('@')[0]}
                                onChange={e => {
                                    const val = e.target.value.split('@')[0].toLowerCase().replace(/\s/g, '');
                                    set("email", val);
                                }}
                                placeholder="jackson"
                                className="w-full pl-4 pr-48 py-2.5 rounded-xl text-sm bg-[#F9FAFB] dark:bg-[#111] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f]/30 focus:border-[#62397f]"
                            />
                            <div className="absolute right-3 px-3 py-1 bg-gray-100 dark:bg-[#262626] rounded-lg border border-[#E5E7EB] dark:border-[#333] text-[#6B7280] dark:text-[#A0A0A0] text-xs font-bold select-none pointer-events-none">
                                @ststephensrentals.com
                            </div>
                        </div>
                    </div>

                    {/* Role (create only) */}
                    {mode === "create" && (
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wide">Role</label>
                            <select
                                value={form.role}
                                onChange={e => set("role", e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl text-sm bg-[#F9FAFB] dark:bg-[#111] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f]/30 focus:border-[#62397f]"
                            >
                                {roles.map(r => (
                                    <option key={r} value={r}>{ROLE_CONFIG[r]?.label || r}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Password (create only) */}
                    {mode === "create" && (
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wide">Password</label>
                            <div className="relative">
                                <input
                                    required
                                    type={showPwd ? "text" : "password"}
                                    value={form.password}
                                    onChange={e => set("password", e.target.value)}
                                    placeholder="Min 6 characters"
                                    className="w-full px-4 py-2.5 pr-10 rounded-xl text-sm bg-[#F9FAFB] dark:bg-[#111] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f]/30 focus:border-[#62397f]"
                                />
                                <button type="button" onClick={() => setShowPwd(v => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]">
                                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose}
                            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#6B7280] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving}
                            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white hover:opacity-90 disabled:opacity-60 transition-all shadow-md shadow-purple-200 dark:shadow-purple-900/20">
                            {saving ? "Saving…" : mode === "create" ? "Create Account" : "Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Change Role Modal ────────────────────────────────────────
function ChangeRoleModal({ target, callerAdmin, onClose, onConfirm }) {
    const roles = allowedRolesForChange(callerAdmin, target);
    const [role, setRole] = useState(roles[0] || "staff");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const handleConfirm = async () => {
        setError(null);
        setSaving(true);
        try { await onConfirm(role); }
        catch (err) { setError(err?.response?.data?.message || "An error occurred."); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-2xl w-full max-w-sm border border-[#E5E7EB] dark:border-[#2A2A2A]">
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                            <ArrowUpDown size={20} className="text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                            <h2 className="font-bold text-[#111] dark:text-white text-base">Change Role</h2>
                            <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">{target.full_name}</p>
                        </div>
                    </div>

                    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                        Changing this admin's role will immediately modify their system access privileges.
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl px-4 py-2 border border-red-200 dark:border-red-800">
                            <AlertTriangle size={14} className="flex-shrink-0" /> {error}
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wide">New Role</label>
                        <select value={role} onChange={e => setRole(e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl text-sm bg-[#F9FAFB] dark:bg-[#111] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f]/30 focus:border-[#62397f]">
                            {roles.map(r => (
                                <option key={r} value={r}>{ROLE_CONFIG[r]?.label || r}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex gap-3 pt-1">
                        <button onClick={onClose}
                            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#6B7280] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors">
                            Cancel
                        </button>
                        <button onClick={handleConfirm} disabled={saving}
                            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:opacity-90 disabled:opacity-60 transition-all">
                            {saving ? "Saving…" : "Confirm Change"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Row Actions Dropdown ─────────────────────────────────────
function RowActions({ row, callerAdmin, onEdit, onToggleStatus, onChangeRole }) {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);
    const can = canActOn(callerAdmin, row);
    const cRank = callerRank(callerAdmin);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    if (!can) {
        return (
            <div className="flex items-center gap-1">
                <Lock size={14} className="text-[#D1D5DB] dark:text-[#444]" />
                <span className="text-xs text-[#D1D5DB] dark:text-[#444]">Locked</span>
            </div>
        );
    }

    return (
        <div className="relative" ref={menuRef}>
            <button onClick={() => setOpen(v => !v)}
                className="p-2 rounded-lg hover:bg-[#F3F4F6] dark:hover:bg-[#262626] transition-colors">
                <MoreVertical size={16} className="text-[#6B7280]" />
            </button>

            {open && (
                <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-[#1E1E1E] rounded-xl shadow-xl border border-[#E5E7EB] dark:border-[#2A2A2A] z-20 overflow-hidden py-1">
                    <button onClick={() => { setOpen(false); onEdit(); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-[#374151] dark:text-[#D1D5DB] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors">
                        <Edit2 size={14} /> Edit
                    </button>

                    {cRank >= 3 && (
                        <button onClick={() => { setOpen(false); onChangeRole(); }}
                            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors">
                            <ArrowUpDown size={14} /> Change Role
                        </button>
                    )}

                    <div className="border-t border-[#F3F4F6] dark:border-[#2A2A2A] my-1" />

                    <button onClick={() => { setOpen(false); onToggleStatus(); }}
                        className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${row.status === "active"
                            ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10"
                            : "text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/10"
                            }`}>
                        <Power size={14} />
                        {row.status === "active" ? "Suspend" : "Activate"}
                    </button>
                </div>
            )}
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────────
export default function AdminManagementPage() {
    const navigate = useNavigate();
    const { admin: callerAdmin } = useAdminAuth();
    const { showNotification } = useNotification();
    const { hasPermission, checkPermission } = useAdminPermissions();

    const [data, setData] = useState([]);
    const [stats, setStats] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    // Filters
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);

    // Modals
    const [modal, setModal] = useState(null); // { type: "create"|"edit"|"role", target? }

    const refresh = () => setRefreshKey(k => k + 1);

    const fetchAdmins = useCallback(async () => {
        setLoading(true);
        try {
            const res = await AdminManagementService.getAll({
                page, limit: 20,
                search: search || undefined,
                role: roleFilter !== "all" ? roleFilter : undefined,
                status: statusFilter !== "all" ? statusFilter : undefined,
            });
            setData(res.data || []);
            setStats(res.stats || null);
            setPagination(res.pagination || null);
        } catch (err) {
            showNotification({
                type: "error",
                message: err?.response?.data?.message || "Failed to load admins.",
            });
        } finally {
            setLoading(false);
        }
    }, [page, search, roleFilter, statusFilter, refreshKey]); // eslint-disable-line

    useEffect(() => {
        if (!hasPermission("super_admin")) {
            showNotification({
                type: "failed",
                title: "Access Restricted",
                message: "You do not have permission to access admin management.",
            });
            navigate("/admin/dashboard");
        }
    }, [hasPermission, navigate, showNotification]);

    useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

    // ── Handlers ───────────────────────────────────────────────
    const handleCreate = async (payload) => {
        const res = await AdminManagementService.create(payload);
        showNotification({ type: "success", message: "Admin account created successfully." });
        setModal(null);
        refresh();
        return res;
    };

    const handleEdit = async (payload) => {
        const res = await AdminManagementService.update(modal.target.id, payload);
        showNotification({ type: "success", message: "Admin updated successfully." });
        setModal(null);
        refresh();
        return res;
    };

    const handleToggleStatus = async (row) => {
        try {
            await AdminManagementService.toggleStatus(row.id);
            const verb = row.status === "active" ? "suspended" : "activated";
            showNotification({ type: "success", message: `Admin account ${verb}.` });
            refresh();
        } catch (err) {
            showNotification({
                type: "error",
                message: err?.response?.data?.message || "Failed to update status.",
            });
        }
    };

    const handleChangeRole = async (newRole) => {
        const res = await AdminManagementService.changeRole(modal.target.id, newRole);
        showNotification({ type: "success", message: "Role changed successfully." });
        setModal(null);
        refresh();
        return res;
    };

    // ── Render ─────────────────────────────────────────────────
    return (
        <AdminLayout>
            <div className="p-6 space-y-6 max-w-[1400px] mx-auto">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#111111] dark:text-white font-poppins">
                            Admin Management
                        </h1>
                        <p className="text-sm text-[#6B7280] dark:text-[#A0A0A0] mt-1 font-inter">
                            Manage system users and permissions
                        </p>
                    </div>
                    {checkPermission("admin") && (
                        <button
                            onClick={() => setModal({ type: "create" })}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white rounded-xl text-sm font-semibold hover:opacity-90 transition-all shadow-lg shadow-purple-200 dark:shadow-purple-900/30"
                        >
                            <Plus size={16} />
                            <span>Create Account</span>
                        </button>
                    )}
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="Total Admins" value={stats.total} icon={UserCog} color="bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f]" />
                        <StatCard label="Super Admins" value={stats.super_admins} icon={ShieldCheck} color="bg-purple-100 dark:bg-purple-900/30 text-purple-600" />
                        <StatCard label="Admins" value={stats.admins} icon={UserCog} color="bg-blue-100 dark:bg-blue-900/30 text-blue-600" />
                        <StatCard label="Staff" value={stats.staff} icon={User} color="bg-green-100 dark:bg-green-900/30 text-green-600" />
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#F3F4F6] dark:border-[#222] p-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        {/* Search */}
                        <div className="relative flex-1">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                            <input
                                value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                                placeholder="Search by name or email…"
                                className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm bg-[#F9FAFB] dark:bg-[#111] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111] dark:text-white placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#62397f]/30 focus:border-[#62397f] transition-all"
                            />
                        </div>

                        <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
                            className="px-4 py-2.5 rounded-xl text-sm bg-[#F9FAFB] dark:bg-[#111] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f]/30 focus:border-[#62397f]">
                            <option value="all">All Roles</option>
                            <option value="super_admin">Super Admin</option>
                            <option value="admin">Admin</option>
                            <option value="staff">Staff</option>
                        </select>

                        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                            className="px-4 py-2.5 rounded-xl text-sm bg-[#F9FAFB] dark:bg-[#111] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f]/30 focus:border-[#62397f]">
                            <option value="all">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="suspended">Suspended</option>
                        </select>

                        <button onClick={refresh}
                            className="p-2.5 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#6B7280] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors">
                            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#F3F4F6] dark:border-[#222] overflow-visible pb-32">
                    {/* Desktop header */}
                    <div className="hidden md:grid grid-cols-[1fr_200px_130px_110px_150px_80px] gap-4 px-6 py-3 bg-[#F9FAFB] dark:bg-[#111] border-b border-[#F3F4F6] dark:border-[#222]">
                        {["Name / Email", "Role", "Status", "Created", "Last Login", ""].map(h => (
                            <span key={h} className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wide">{h}</span>
                        ))}
                    </div>

                    {/* Rows */}
                    {loading ? (
                        <div className="flex items-center justify-center py-20 text-[#6B7280] dark:text-[#A0A0A0]">
                            <RefreshCw size={24} className="animate-spin mr-3" /> Loading…
                        </div>
                    ) : data.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-[#6B7280] dark:text-[#A0A0A0] gap-2">
                            <UserCog size={36} className="opacity-30" />
                            <p className="text-sm">No admins found.</p>
                        </div>
                    ) : (
                        <div>
                            {data.map(row => {
                                const statusCfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.active;
                                const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
                                const fmtLogin = (iso) => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }).replace(",", " ·") : "Never";

                                return (
                                    <div key={row.id}>
                                        {/* Desktop row */}
                                        <div className="hidden md:grid grid-cols-[1fr_200px_130px_110px_150px_80px] gap-4 px-6 py-4 border-b border-[#F3F4F6] dark:border-[#222] hover:bg-[#F9FAFB] dark:hover:bg-[#151515] transition-colors items-center">
                                            <div>
                                                <p className="text-sm font-semibold text-[#111] dark:text-white">{row.full_name}</p>
                                                <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">{row.email}</p>
                                            </div>
                                            <RoleBadge row={row} />
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusCfg.color}`}>
                                                <CheckCircle size={11} /> {statusCfg.label}
                                            </span>
                                            <span className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">{fmtDate(row.created_at)}</span>
                                            <span className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">{fmtLogin(row.last_login_at)}</span>
                                            <div className="flex justify-end">
                                                <RowActions
                                                    row={row}
                                                    callerAdmin={callerAdmin}
                                                    onEdit={() => setModal({ type: "edit", target: row })}
                                                    onToggleStatus={() => handleToggleStatus(row)}
                                                    onChangeRole={() => setModal({ type: "role", target: row })}
                                                />
                                            </div>
                                        </div>

                                        {/* Mobile card */}
                                        <div className="md:hidden border-b border-[#F3F4F6] dark:border-[#222] px-4 py-4 space-y-3">
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <p className="font-semibold text-[#111] dark:text-white text-sm">{row.full_name}</p>
                                                    <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">{row.email}</p>
                                                </div>
                                                <RowActions
                                                    row={row}
                                                    callerAdmin={callerAdmin}
                                                    onEdit={() => setModal({ type: "edit", target: row })}
                                                    onToggleStatus={() => handleToggleStatus(row)}
                                                    onChangeRole={() => setModal({ type: "role", target: row })}
                                                />
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <RoleBadge row={row} />
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusCfg.color}`}>
                                                    <CheckCircle size={10} /> {statusCfg.label}
                                                </span>
                                            </div>
                                            <div className="flex gap-4 text-xs text-[#6B7280] dark:text-[#A0A0A0]">
                                                <span>Created: {fmtDate(row.created_at)}</span>
                                                <span>Login: {fmtLogin(row.last_login_at)}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">
                            Showing {((page - 1) * pagination.limit) + 1}–{Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
                        </p>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                                className="p-2 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#6B7280] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] disabled:opacity-40 transition-colors">
                                <ChevronLeft size={16} />
                            </button>
                            <span className="text-sm font-medium text-[#111] dark:text-white">{page} / {pagination.pages}</span>
                            <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page >= pagination.pages}
                                className="p-2 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#6B7280] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] disabled:opacity-40 transition-colors">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {modal?.type === "create" && (
                <AdminModal mode="create" callerAdmin={callerAdmin} onClose={() => setModal(null)} onSave={handleCreate} />
            )}
            {modal?.type === "edit" && (
                <AdminModal mode="edit" initial={modal.target} callerAdmin={callerAdmin} onClose={() => setModal(null)} onSave={handleEdit} />
            )}
            {modal?.type === "role" && (
                <ChangeRoleModal target={modal.target} callerAdmin={callerAdmin} onClose={() => setModal(null)} onConfirm={handleChangeRole} />
            )}
        </AdminLayout>
    );
}
